import React, { useEffect, useState } from 'react';
import './styles/ui-tab-bar.scss';
import UIIcon from '../UIIcon';
import { BackIcon, ForwardIcon } from '../../../assets/icons';
import UIScrollTrigger from '../UIScrollTrigger/UIScrollTrigger';
import { ZendeskAPI } from 'react-zendesk';
import scrollIntoView from 'scroll-into-view';
import { LEFT_ARROW, RIGHT_ARROW } from './config';

export default function UITabBar({ selectedTab, containerClassName, tabs, tabKey, updateSelected }: UITabBarProps) {
	const [remainingScrollAmount, setRemainingScrollAmount] = useState<number>(0);

	const onSelectTab = (selectedTab: PLTab) => {
		if (!selectedTab) return;
		document.getElementById(selectedTab.value)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		updateSelected(selectedTab);
	};

	useEffect(() => {
		onSelectTab(selectedTab);
	}, [selectedTab]);

	if (!selectedTab) return null;

	const calculateRemainingScrollAmount = (el: HTMLDivElement) => {
		if (!el) return;
		const calculatedRemainingScrollAmount = el.scrollWidth - el.clientWidth;
		if (calculatedRemainingScrollAmount !== calculatedRemainingScrollAmount) setRemainingScrollAmount(calculatedRemainingScrollAmount);
	};

	const getShouldShowArrows = () => {
		return remainingScrollAmount > 0;
	};

	const onClickTab = (e: React.MouseEvent, id: string) => {
		e.stopPropagation();
		const element = document.getElementById(id);
		if (element) scrollIntoView(element);
	};

	const renderLeftArrow = () => {
		if (!getShouldShowArrows()) {
			return;
		}

		return (
			<UIScrollTrigger
				tweenFromProps={LEFT_ARROW.tweenFrom}
				triggerId={'scrollStartTab'}
				targetId={'left'}
				markers={false}
				horizontal={true}
				scroller={LEFT_ARROW.scroller}
				start={LEFT_ARROW.start}
				end={LEFT_ARROW.end}
				tweenToProps={LEFT_ARROW.tweenTo}>
				<div className='tabScrollbar__leftArrow' id={'left'} onClick={(e) => onClickTab(e, 'scrollStartTab')}>
					<UIIcon className='tabScrollbar__leftArrow--back' alt={'back'} iconSrc={BackIcon.white} />
				</div>
			</UIScrollTrigger>
		);
	};

	const renderRightArrow = () => {
		if (!getShouldShowArrows()) {
			return;
		}

		return (
			<UIScrollTrigger
				tweenFromProps={RIGHT_ARROW.tweenFrom}
				triggerId={'scrollEndTab'}
				targetId={'right'}
				markers={false}
				horizontal={true}
				scroller={RIGHT_ARROW.scroller}
				start={RIGHT_ARROW.start}
				end={RIGHT_ARROW.end}
				tweenToProps={RIGHT_ARROW.tweenTo}>
				<div className='tabScrollbar__rightArrow' id={'right'} onClick={(e) => onClickTab(e, 'scrollEndTab')}>
					<UIIcon className='tabScrollbar__rightArrow--forward' iconSrc={ForwardIcon.white} alt={'forward'} />
				</div>
			</UIScrollTrigger>
		);
	};

	const renderTab = (tab: PLTab) => {
		const isSelected = selectedTab.value === tab.value;

		const containerDefaultClassName = isSelected ? `tab-container--tabSelected` : `tab-container--tabUnselected`;
		let textClassname = isSelected ? `tab-container--tabSelected--text` : `tab-container--tabUnselected--text`;

		if (tab.highlight && tabs.length > 1) {
			textClassname = `${textClassname} tab-container--tabHighlightedText`;
		}
		const barClassName = isSelected ? 'fade-in-div tab-container--tabSelected--bar' : 'fade-in-div tab-container--tabUnselected--bar';
		return (
			<div id={`${tab.value}`} key={`${selectedTab.value}${tabKey}${tab.text}`} className={`${containerDefaultClassName}`} onClick={() => onSelectTab(tab)}>
				<p className={textClassname}>{tab.text}</p>
				<div className={barClassName} />
			</div>
		);
	};

	if (!selectedTab) {
		return <div key={JSON.stringify(selectedTab)} />;
	}

	return (
		<div className={`tab ${containerClassName}`}>
			<div id={'tabScrollbar'} className={`tabScrollbar`} ref={calculateRemainingScrollAmount}>
				<div id={'scrollStartTab'} />
				{tabs.map(renderTab)}
				<div id={'scrollEndTab'} />
			</div>

			{renderLeftArrow()}
			{renderRightArrow()}
		</div>
	);
}
